import React from "react";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputAdornment,
  TextField,
} from "@mui/material";
import {
  OnboardingProgressDiv,
  isCameraCheckSupported,
} from "../Onboarding/OnboardingProgressDiv";
import ErrorBoundary from "../../../ErrorBoundary";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useAddSiteOnboardingStore } from "./addSiteOnboardingStore";
import { createApi } from "../../utils/createApi";
import { AxiosError } from "axios";

const ExpandableHelperText = ({
  text,
  contentTestId,
}: {
  text: string;
  contentTestId?: string;
}) => {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <span className="font-mulish text-[12px] text-[#6B7280]">
      <span
        data-testid={contentTestId}
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: expanded ? "unset" : 1,
          overflow: "hidden",
        }}
      >
        {text}
      </span>
      <button
        type="button"
        className="font-mulish mt-1 p-0 text-[12px] text-[#283E6D] underline"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? "Show less" : "Read more"}
      </button>
    </span>
  );
};

export const WireGuardSetupScreen = ({
  setVisibleScreen,
}: {
  setVisibleScreen: (screen: React.ReactNode) => void;
}) => {
  const [postObject, setPostObject] = React.useState({
    name: "",
    client_public_key: "",
    client_tunnel_ip: "",
    subnets: "",
    subnets_are_virtually_assigned: false,
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [isClientTunnelIpLoading, setIsClientTunnelIpLoading] =
    React.useState(true);
  const [cameraCount, setCameraCount] = React.useState("");
  const [cidrPrefixLength, setCidrPrefixLength] = React.useState("24");
  const [isSuggestingSubnet, setIsSuggestingSubnet] = React.useState(false);
  const [subnetSuggestionError, setSubnetSuggestionError] = React.useState<
    string | null
  >(null);
  const [showFailureDialog, setShowFailureDialog] = React.useState(false);
  const [failureErrorMessage, setFailureErrorMessage] = React.useState<
    string | null
  >(null);

  const parseSubnetsInput = React.useCallback((subnetsInput: string) => {
    const trimmed = subnetsInput.trim();
    if (!trimmed) {
      return [];
    }
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === "string" ? item.trim() : ""))
            .filter(Boolean);
        }
      } catch (error) {
        // Fall through to comma-separated parsing.
      }
    }
    return trimmed
      .split(",")
      .map((value) =>
        value
          .trim()
          .replace(/^\[/, "")
          .replace(/\]$/, "")
          .replace(/^['"]/, "")
          .replace(/['"]$/, "")
      )
      .filter(Boolean);
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const fetchSuggestedClientTunnelIp = async () => {
      const AxiosUI = createApi("");
      try {
        const response = await AxiosUI.get<{
          client_tunnel_ip?: string;
        }>("wireguard_tunnel/suggest-next-client-ip/");
        const suggestedClientTunnelIp = response?.data?.client_tunnel_ip;
        if (!isMounted || !suggestedClientTunnelIp) {
          return;
        }
        setPostObject((prev) => ({
          ...prev,
          client_tunnel_ip: suggestedClientTunnelIp,
        }));
      } catch (error) {
        // If suggestion fetch fails, allow manual entry.
      } finally {
        if (isMounted) {
          setIsClientTunnelIpLoading(false);
        }
      }
    };

    fetchSuggestedClientTunnelIp();

    return () => {
      isMounted = false;
    };
  }, []);

  const goToOnboardingProgress = () =>
    setVisibleScreen(
      <OnboardingProgressDiv
        percentage={
          isCameraCheckSupported()
            ? localStorage.healthMonitoringOnly === "true"
              ? 33
              : 25
            : 33
        }
        step={1}
        setVisibleScreen={setVisibleScreen}
      />
    );

  const handleSubmit = async () => {
    const wireguardServer =
      useAddSiteOnboardingStore.getState().selectedWireGuardServer ?? "";
    let selectedGroupId: number | undefined;
    try {
      const rawValues = localStorage.getItem("values");
      if (rawValues) {
        const parsedValues = JSON.parse(rawValues) as { group?: unknown };
        const groupValue = parsedValues?.group;
        if (
          (typeof groupValue === "string" && groupValue.trim()) ||
          typeof groupValue === "number"
        ) {
          const parsedGroupId = Number(groupValue);
          selectedGroupId = Number.isFinite(parsedGroupId)
            ? parsedGroupId
            : undefined;
        }
      }
    } catch (error) {
      // Keep submission flow working if localStorage values is malformed.
    }
    const subnetsArray = parseSubnetsInput(postObject.subnets);
    const payload = {
      name: postObject.name,
      wireguard_server: wireguardServer,
      group_id: selectedGroupId,
      client_public_key: postObject.client_public_key,
      client_tunnel_ip: postObject.client_tunnel_ip || undefined,
      subnets: subnetsArray,
      subnets_are_virtually_assigned: postObject.subnets_are_virtually_assigned,
    };
    setSubmitting(true);
    const AxiosUI = createApi("");
    try {
      await AxiosUI.post("wireguard_tunnel/", payload);
      goToOnboardingProgress();
    } catch (reason) {
      setSubmitting(false);
      const err = reason as AxiosError<{ error?: string; detail?: string }>;
      const message =
        typeof err.response?.data === "object" && err.response?.data !== null
          ? (err.response.data as { error?: string; detail?: string }).error ??
            (err.response.data as { error?: string; detail?: string }).detail
          : typeof err.response?.data === "string"
            ? err.response.data
            : null;
      setFailureErrorMessage(message ?? null);
      setShowFailureDialog(true);
      return;
    }
    setSubmitting(false);
  };

  const handleSuggestNextSubnet = async () => {
    const AxiosUI = createApi("");
    setSubnetSuggestionError(null);
    setIsSuggestingSubnet(true);
    try {
      const trimmedCameraCount = cameraCount.trim();
      const trimmedPrefixLength = cidrPrefixLength.trim().replace("/", "");
      const queryParam = trimmedCameraCount
        ? `camera_count=${encodeURIComponent(trimmedCameraCount)}`
        : `prefix_length=${encodeURIComponent(trimmedPrefixLength || "24")}`;
      const response = await AxiosUI.get<{ subnet?: string }>(
        `wireguard_tunnel/suggest-next-subnet/?${queryParam}`
      );
      const suggestedSubnet = response?.data?.subnet;
      if (!suggestedSubnet) {
        setSubnetSuggestionError("Unable to suggest a subnet right now.");
        return;
      }
      setPostObject((prev) => ({
        ...prev,
        subnets: `["${suggestedSubnet}"]`,
      }));
    } catch (error) {
      setSubnetSuggestionError("Unable to suggest a subnet right now.");
    } finally {
      setIsSuggestingSubnet(false);
    }
  };

  return (
    <ErrorBoundary>
      <div>
        <p
          className={
            "font-mulish absolute top-[90px] w-[300px] font-bold text-[#283E6D] left-[25px] text-[16px]"
          }
        >
          WireGuard Configuration
        </p>
        <div className="pt-[140px] pl-[30px]">
          <TextField
            value={postObject.name}
            id="wireguard_name"
            variant="standard"
            label="Name"
            required
            onChange={(event) => {
              setPostObject((prev) => ({
                ...prev,
                name: event.target.value,
              }));
            }}
            sx={{ width: "400px" }}
            type="string"
          />
          <br />
          <br />
          <TextField
            value={postObject.client_public_key}
            id="client_public_key"
            variant="standard"
            label="Client Public Key"
            required
            helperText="WireGuard client public key (must be unique)"
            onChange={(event) => {
              setPostObject((prev) => ({
                ...prev,
                client_public_key: event.target.value,
              }));
            }}
            sx={{ width: "400px" }}
            type="string"
          />
          <br />
          <br />
          <TextField
            value={postObject.client_tunnel_ip}
            id="client_tunnel_ip"
            variant="standard"
            label="Client Tunnel IP"
            helperText={
              <ExpandableHelperText
                text="Client tunnel IP address (must end with /32, e.g., '10.0.0.1/32'). By default this is pre-populated with the first available IP from the pool, but you can change it if the client already has one assigned."
                contentTestId="client-tunnel-ip-helper-content"
              />
            }
            disabled={isClientTunnelIpLoading}
            InputProps={{
              endAdornment: isClientTunnelIpLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            }}
            onChange={(event) => {
              setPostObject((prev) => ({
                ...prev,
                client_tunnel_ip: event.target.value,
              }));
            }}
            sx={{ width: "400px" }}
            type="string"
          />
          <br />
          <br />
          <TextField
            value={postObject.subnets}
            id="subnets"
            label="Subnets"
            multiline
            minRows={2}
            onChange={(event) => {
              setPostObject((prev) => ({
                ...prev,
                subnets: event.target.value,
              }));
            }}
            sx={{ width: "400px" }}
            helperText={
              <ExpandableHelperText
                text={`The range of IP addresses on the customer's local network, in CIDR format (e.g., ["192.168.1.0/24"]). If the NVR/recording server is on the same machine as the WireGuard client, you can leave this empty. If the cameras are on a different IP range (e.g., the client routes traffic across their network), enter the local subnet(s) where their cameras live. Always use the smallest subnet that covers the required hosts - a /32 for a single IP is better than a /24 for an entire block.`}
              />
            }
          />
          <div className="pl-[30px] mt-3 flex w-[400px] gap-4">
            <TextField
              value={cameraCount}
              id="camera_count"
              label="Approx camera count"
              type="number"
              onChange={(event) => {
                setSubnetSuggestionError(null);
                setCameraCount(event.target.value);
              }}
              placeholder="-"
              sx={{ width: "192px" }}
              helperText="Leave blank if unknown"
            />
            <TextField
              value={cidrPrefixLength}
              id="cidr_size"
              label="CIDR size"
              type="number"
              onChange={(event) => {
                setSubnetSuggestionError(null);
                setCidrPrefixLength(event.target.value);
              }}
              sx={{ width: "92px" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">/</InputAdornment>
                ),
              }}
            />
          </div>
          <div className="pl-[30px] mt-2">
            <OrangeTextButton
              onClick={handleSuggestNextSubnet}
              text={isSuggestingSubnet ? "Suggesting…" : "Suggest Next Subnet"}
              disabled={isSuggestingSubnet}
            />
          </div>
          {subnetSuggestionError && (
            <FormHelperText
              className="font-mulish mt-1 text-red-600"
              role="alert"
            >
              {subnetSuggestionError}
            </FormHelperText>
          )}
          <div>
            <FormControlLabel
              sx={{
                fontFamily: "Mulish",
                color: "#001943",
              }}
              label="Virtually Assign Subnets"
              control={
                <Checkbox
                  color="primary"
                  checked={postObject.subnets_are_virtually_assigned}
                  onChange={(event, checked) => {
                    setPostObject((prev) => ({
                      ...prev,
                      subnets_are_virtually_assigned: checked,
                    }));
                  }}
                />
              }
            />
            <FormHelperText>
              <ExpandableHelperText
                text={`Check this if the customer's real subnet overlaps with an existing tunnel's subnet. When enabled, the customer sets up DNAT/SNAT on their side to translate traffic to a virtual IP range, and you enter that virtual range in the Subnets field above. This avoids routing conflicts while keeping both tunnels operational.`}
              />
            </FormHelperText>
          </div>
        </div>
        <div className="pt-10 pl-[420px]">
          <div style={{ display: "flex" }}>
            &nbsp;&nbsp;&nbsp;
            <OrangeTextButton
              onClick={handleSubmit}
              text={submitting ? "Submitting…" : "Submit"}
              disabled={
                submitting ||
                !postObject.name.trim() ||
                !postObject.client_public_key.trim()
              }
            />
          </div>
        </div>
      </div>
      <Dialog
        open={showFailureDialog}
        onClose={() => {
          setShowFailureDialog(false);
          setFailureErrorMessage(null);
        }}
        aria-labelledby="wireguard-failure-dialog-title"
        aria-describedby="wireguard-failure-dialog-description"
      >
        <DialogTitle
          id="wireguard-failure-dialog-title"
          sx={{ fontFamily: "Mulish" }}
        >
          WireGuard configuration could not be saved
        </DialogTitle>
        <DialogContent sx={{ fontFamily: "Mulish" }}>
          {failureErrorMessage && (
            <p
              className="font-mulish mb-3 text-[14px] text-red-600"
              role="alert"
            >
              {failureErrorMessage}
            </p>
          )}
          <p id="wireguard-failure-dialog-description">
            Would you like to try again or skip this step and continue with
            onboarding?
          </p>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <OrangeTextButton
            onClick={() => {
              setShowFailureDialog(false);
              setFailureErrorMessage(null);
            }}
            text="Try Again"
            disabled={false}
          />
          <OrangeTextButton
            onClick={() => {
              setShowFailureDialog(false);
              setFailureErrorMessage(null);
              goToOnboardingProgress();
            }}
            text="Next Step"
            disabled={false}
          />
        </DialogActions>
      </Dialog>
    </ErrorBoundary>
  );
};
